import type { TFunction } from 'i18next';
// `i18n.exists` is a static dictionary lookup, not a render-time translation.
// It is intentionally NOT migrated through `useTranslation` — its result does
// not depend on the active language, only on whether the key is present in
// the dictionary. See `getUnitsLabel` and `formatMeasureName` below for the
// runtime-composed key fallback this enables.
import i18n from 'i18next';
import type { Portfolio } from '../../api/portfolio-client/portfolio-client.type';
import type { ProjectsResponse } from '../../api/projects-client/projects-client.type';
import type { MeasureUnits } from '../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../helpers/date-time-helpers';
import { capitalizeFirstLetter } from '../../helpers/string-helpers/string-helpers';
import type { InitiativeOption } from '../../store/process-analysis-store/process-analysis-store.type';
import type { FloraContextObject, FloraContextualInfo } from '../flora/flora.type';
import { FloraSupportedObjectTypes } from '../flora/flora.type';
import { GeneralViewNames } from '../side-bar/side-bar.type';
import { Tab } from './process-analysis.type';

/**
 * Formats units label for display
 *
 * @param {MeasureUnits | null} units - The measure units
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} Formatted units label
 */
const getUnitsLabel = (units: MeasureUnits | null, t: TFunction): string => {
  if (!units) {
    return '';
  }

  if (units.toLowerCase() === 'tasks') {
    return t('common.work_units');
  }

  if (units.toLowerCase() === 'points') {
    return t('common.estimation');
  }

  // Guard the dynamic lookup with i18n.exists so a missing-in-dictionary
  // unit returns the raw string instead of triggering the Vitest-time
  // `missingKey` handler (which throws to catch typo'd keys). This is a
  // documented fallback for custom unit enums that ship from the API.
  // The `i18n.exists` check still uses the i18next module directly because
  // it is not part of the React render lifecycle — only the actual
  // translation lookup needs to flow through `t`.
  const unitKey = `process_analysis.units.${units.toLowerCase()}`;
  // The dynamic key forces a cast off the strict TFunction key narrowing
  // here — same pattern as elsewhere in the codebase for runtime keys.
  return i18n.exists(unitKey) ? (t as (key: string) => string)(unitKey) : units;
};

/**
 * Formats a measure name by either looking up the translation or converting from snake_case
 *
 * @param {string} measure - The measure name to format (e.g. "cycle_time", "lead_time")
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The formatted measure name, either from translation or capitalized words
 */
const formatMeasureName = (measure: string, t: TFunction): string => {
  const translationKey = `process_analysis.measures.${measure}`;

  // Guard the dynamic lookup with i18n.exists so a missing-in-dictionary
  // measure skips straight to the snake_case → Title Case fallback instead
  // of triggering the Vitest-time `missingKey` handler (which throws to
  // catch typo'd keys). Custom measure names are an established API input
  // — they are NOT added to the dictionary — so this path is expected
  // in production and must not be treated as a coverage failure.
  if (!i18n.exists(translationKey)) {
    return measure.split('_').map(capitalizeFirstLetter).join(' ');
  }

  return (t as (key: string, opts?: Record<string, unknown>) => string)(translationKey, {
    estimation: t('common.estimation'),
    work_unit: t('common.work_unit'),
    work_units: t('common.work_units'),
  });
};

/**
 * Returns the default selection of initiatives, sorted alphabetically by name and limited to the first four.
 *
 * @param {InitiativeOption[]} initiatives - The list of initiative options to select from.
 * @returns {InitiativeOption[]} The first four initiatives sorted by name.
 */
const getDefaultInitiativeSelection = (initiatives: InitiativeOption[]): InitiativeOption[] => {
  return [...initiatives].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 4);
};

/**
 * Collects the project (team) ids that exist in the active portfolio's project list.
 *
 * The list comes from `GET /portfolios/{id}/projects/`, which is both user-scoped
 * and filtered to configured boards — the same source the project selector builds
 * its options from — so it is the authoritative answer to "can this id still be
 * selected?".
 *
 * @param {ProjectsResponse[]} teams - Projects returned for the active portfolio
 * @returns {Set<string>} Set of selectable project ids
 * @example
 * getAvailableTeamIds([{ id: 'p1', name: 'Platform', subprojects: [] }]); // Set { 'p1' }
 */
const getAvailableTeamIds = (teams: ProjectsResponse[]): Set<string> => {
  return new Set(teams.map(({ id }) => id));
};

/**
 * Collects the board (subproject) ids that exist in the active portfolio's project list.
 *
 * @param {ProjectsResponse[]} teams - Projects returned for the active portfolio
 * @returns {Set<string>} Set of selectable board ids across every project
 * @example
 * getAvailableBoardIds([{ id: 'p1', name: 'Platform', subprojects: [{ id: 'b1' }] }]); // Set { 'b1' }
 */
const getAvailableBoardIds = (teams: ProjectsResponse[]): Set<string> => {
  return new Set(teams.flatMap(({ subprojects }) => (subprojects ?? []).map(({ id }) => id)));
};

/**
 * Creates Flora contextual info from process analysis store state
 *
 * @param {Tab} activeTab - The currently active tab (portfolios, teams, or boards)
 * @param {string[]} selectedPortfolios - Array of selected portfolio IDs
 * @param {string[]} selectedTeams - Array of selected team IDs
 * @param {string[]} selectedBoards - Array of selected board IDs
 * @param {string} startDate - The start date in ISO format
 * @param {string} endDate - The end date in ISO format
 * @param {Portfolio | null} portfolio - The current portfolio
 * @param {Portfolio[]} portfolios - Array of all portfolios
 * @param {ProjectsResponse[]} teams - Array of teams/projects
 * @param {string[]} selectedMeasures - Array of selected measure names (optional, for measure comparison mode)
 * @returns {FloraContextualInfo} Flora contextual info with current objects and date range
 */
function getProcessAnalysisFloraContext(
  activeTab: Tab,
  selectedPortfolios: string[],
  selectedTeams: string[],
  selectedBoards: string[],
  startDate: string,
  endDate: string,
  portfolio: Portfolio | null,
  portfolios: Portfolio[],
  teams: ProjectsResponse[],
  selectedMeasures?: string[],
): FloraContextualInfo {
  const objects: FloraContextObject[] = [];

  // Add context objects based on active tab and selections
  switch (activeTab) {
    case Tab.Portfolios:
      // For portfolios tab, add current portfolio first
      if (portfolio) {
        objects.push({
          type: FloraSupportedObjectTypes.Portfolio,
          id: portfolio.id,
          name: portfolio.name,
        });
      }
      // Then add all selected portfolios
      if (selectedPortfolios.length > 0) {
        for (const portfolioId of selectedPortfolios) {
          // Skip if already added (current portfolio)
          if (portfolio && portfolioId === portfolio.id) {
            continue;
          }
          const selectedPortfolio = portfolios.find((p) => p.id === portfolioId);
          if (selectedPortfolio) {
            objects.push({
              type: FloraSupportedObjectTypes.Portfolio,
              id: selectedPortfolio.id,
              name: selectedPortfolio.name,
            });
          }
        }
      }
      break;

    case Tab.Teams:
      // For teams tab, add selected teams as projects
      if (selectedTeams.length > 0) {
        for (const teamId of selectedTeams) {
          const team = teams.find((t) => t.id === teamId);
          if (team) {
            objects.push({
              type: FloraSupportedObjectTypes.Project,
              id: team.id,
              name: team.name,
            });
          }
        }
      }
      break;

    case Tab.Boards:
      // For boards tab, add selected boards as subprojects
      if (selectedBoards.length > 0) {
        for (const boardId of selectedBoards) {
          // Find the board across all teams
          for (const team of teams) {
            const board = team.subprojects.find((sp) => sp.id === boardId);
            if (board) {
              objects.push({
                type: FloraSupportedObjectTypes.Subproject,
                id: board.id,
                name: board.name,
              });
              break;
            }
          }
        }
      }
      break;
  }

  // If no specific entities are selected, default to portfolio
  if (objects.length === 0 && portfolio) {
    objects.push({
      type: FloraSupportedObjectTypes.Portfolio,
      id: portfolio.id,
      name: portfolio.name,
    });
  }

  return {
    currentModule: GeneralViewNames.ProcessAnalysis,
    currentObjects: objects,
    startDate: formatToISODate(startDate),
    endDate: formatToISODate(endDate),
    selectedMeasures: selectedMeasures && selectedMeasures.length > 0 ? selectedMeasures : undefined,
  };
}

export {
  formatMeasureName,
  getAvailableBoardIds,
  getAvailableTeamIds,
  getDefaultInitiativeSelection,
  getProcessAnalysisFloraContext,
  getUnitsLabel,
};
